import { applySiteStudyArea } from '../siteStudyArea';
import polygonClipping from 'polygon-clipping';

import type {
  EngineeringVerdict,
  GenerationImpactFootprintProjection,
  GenerationImpactScenarioSnapshot,
  GenerationImpactSedAuthoritySnapshot,
  TestFitAuthorityRef,
} from '../../types/generationImpact';
import type { SiteEntity, SiteLayer } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteRagReport } from '../../types/siteRag';
import {
  parseReportLeafPath,
  reportLeafLabel,
  withheldNarrativeLeaves,
} from '../report/withheldNarrative';
import { polygonArea, unionPolygonAreaAcres } from '../siteBoundaryGeometry';
import {
  createGenerationImpactParcelBoundarySnapshot,
  sameGenerationImpactParcelBoundary,
} from './boundaryAuthority';
import { resolveGenerationImpactSedAuthority } from './sedAuthority';

const DEFAULT_SETBACK_ALLOWANCE_FRACTION = 0.10;
const AREA_TOLERANCE_ACRES = 0.01;

type TestFitSite = Pick<
  SiteEntity,
  'siteBoundaryAggregate' | 'siteBoundaries' | 'siteBoundary' | 'siteArea' | 'studyArea' | 'siteLayers'
>;

export interface GenerationImpactTestFitProjectionInput {
  scenario: GenerationImpactScenarioSnapshot;
  site?: TestFitSite | null;
  sed?: SiteEvidenceDocument | null;
  authority?: GenerationImpactSedAuthoritySnapshot | null;
}

type TestFitAuthoritySite = TestFitSite & Pick<SiteEntity, 'generationImpactLayoutHead' | 'projectIntent'>;

/**
 * Enforce the workflow contract at report dispatch/save time. Legacy sites
 * with no promoted scenario and no versioned layout remain allowed; once
 * either workflow pointer exists, Test Fit requires the matching valid SED
 * authority tuple and never falls back to live intent or mutable map layers.
 */
export function requireGenerationImpactTestFitAuthority(
  site: TestFitAuthoritySite,
  promotedScenario: GenerationImpactScenarioSnapshot | null | undefined,
  sed: SiteEvidenceDocument | null | undefined,
): GenerationImpactSedAuthoritySnapshot | null {
  const promotedId = site.projectIntent?.promotedGenerationScenarioId;
  const head = site.generationImpactLayoutHead;
  if (!promotedId && !head) return null;
  if (!promotedScenario) {
    throw new Error('Test Fit requires a valid promoted Generation & Impact scenario. Save and promote the scenario, then rebuild the SED.');
  }
  if (promotedId !== promotedScenario.id
    || site.projectIntent?.promotedGenerationInputHash !== promotedScenario.inputHash
    || site.projectIntent?.promotedGenerationResultHash !== promotedScenario.resultHash
    || !promotedScenario.promotedAt
    || (site.projectIntent?.promotedGenerationPromotedAt ?? undefined) !== (promotedScenario.promotedAt ?? undefined)) {
    throw new Error('Test Fit promoted scenario is stale relative to Project Intent. Promote the intended scenario revision and rebuild the SED.');
  }
  if (!head) {
    throw new Error('Test Fit requires a published Generation & Impact layout. Publish the promoted scenario, then rebuild the SED.');
  }
  const resolved = resolveGenerationImpactSedAuthority(sed);
  if (resolved.status !== 'valid') {
    const detail = resolved.status === 'invalid' ? ` (${resolved.reason})` : '';
    throw new Error(`Test Fit requires a rebuilt SED containing the promoted scenario and published layout${detail}.`);
  }
  const { snapshot } = resolved;
  if (snapshot.scenarioSnapshot.id !== promotedScenario.id
    || snapshot.scenarioSnapshot.updatedAt !== promotedScenario.updatedAt
    || snapshot.scenarioSnapshot.inputHash !== promotedScenario.inputHash
    || snapshot.scenarioSnapshot.resultHash !== promotedScenario.resultHash
    || snapshot.scenarioSnapshot.result.modelVersion !== promotedScenario.result.modelVersion
    || (snapshot.scenarioSnapshot.promotedAt ?? undefined) !== (promotedScenario.promotedAt ?? undefined)) {
    throw new Error('Test Fit SED authority is stale relative to the promoted Generation & Impact scenario. Rebuild the SED.');
  }
  const publication = snapshot.layoutPublication;
  if (publication.schemaVersion !== head.schemaVersion
    || publication.publicationId !== head.publicationId
    || publication.version !== head.version
    || publication.scenarioId !== head.scenarioId
    || publication.scenarioName !== head.scenarioName
    || publication.scenarioUpdatedAt !== head.scenarioUpdatedAt
    || publication.inputHash !== head.inputHash
    || publication.resultHash !== head.resultHash
    || publication.modelVersion !== head.modelVersion
    || publication.layoutHash !== head.layoutHash
    || publication.publishedAt !== head.publishedAt
    || publication.publishedBy !== head.publishedBy
    || JSON.stringify(publication.layerIds) !== JSON.stringify(head.layerIds)
    || snapshot.provenance.layoutPublicationId !== head.publicationId
    || snapshot.provenance.layoutVersion !== head.version
    || snapshot.provenance.layoutHash !== head.layoutHash
    || snapshot.provenance.scenarioId !== head.scenarioId
    || snapshot.provenance.inputHash !== head.inputHash
    || snapshot.provenance.resultHash !== head.resultHash) {
    throw new Error('Test Fit SED authority is stale relative to the active published layout. Rebuild the SED.');
  }
  let currentBoundary;
  try {
    currentBoundary = createGenerationImpactParcelBoundarySnapshot(site);
  } catch (error) {
    const detail = error instanceof Error ? ` ${error.message}` : '';
    throw new Error(`Test Fit requires a valid current parcel boundary. Fix the boundary and rebuild the SED.${detail}`);
  }
  if (!sameGenerationImpactParcelBoundary(currentBoundary, snapshot.parcelBoundary)) {
    throw new Error('Test Fit SED authority is stale relative to the current parcel boundary. Rebuild the SED.');
  }
  return snapshot;
}

interface AcreageBasis {
  parcelAreaAcres: number | null;
  parcelAreaSource: NonNullable<GenerationImpactFootprintProjection['parcelAreaSource']>;
  wetlandAcres: number | null;
  floodOverlapAcres: number | null;
  setbackAllowanceAcres: number | null;
  netBuildableAcres: number | null;
  netBuildableBasis: NonNullable<GenerationImpactFootprintProjection['netBuildableBasis']>;
  constraintDataComplete: boolean;
}

interface GeometryFit {
  verdict: EngineeringVerdict;
  basis: NonNullable<GenerationImpactFootprintProjection['spatialFitBasis']>;
  outsideBoundaryAcres: number | null;
  representedFootprintAcres: number | null;
  overlappingFootprintAcres: number | null;
  roles: string[];
  reason: string;
}

function finiteNonNegative(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

function finitePositive(value: unknown): number | null {
  const parsed = finiteNonNegative(value);
  return parsed != null && parsed > 0 ? parsed : null;
}

function resolveParcelArea(site: TestFitSite | null | undefined, sed: SiteEvidenceDocument | null | undefined): {
  acres: number | null;
  source: AcreageBasis['parcelAreaSource'];
} {
  const aggregate = finitePositive(site?.siteBoundaryAggregate?.totalArea);
  if (aggregate != null) return { acres: aggregate, source: 'site-boundary-aggregate' };

  if (site?.siteBoundaries && site.siteBoundaries.length > 0) {
    try {
      const union = finitePositive(unionPolygonAreaAcres(site.siteBoundaries));
      if (union != null) return { acres: union, source: 'site-boundaries' };
    } catch {
      // Malformed legacy rings fall through to the persisted acreage aliases.
    }
  }

  const siteArea = finitePositive(site?.siteArea);
  if (siteArea != null) return { acres: siteArea, source: 'site-area' };
  const profileArea = finitePositive(sed?.siteProfile?.data?.acres);
  if (profileArea != null) return { acres: profileArea, source: 'sed-site-profile' };
  const parcelArea = finitePositive(sed?.parcel?.data?.acres);
  if (parcelArea != null) return { acres: parcelArea, source: 'sed-parcel' };
  return { acres: null, source: 'unavailable' };
}

function resolveWetlandAcres(sed: SiteEvidenceDocument | null | undefined): {
  acres: number | null;
  complete: boolean;
} {
  const environmental = sed?.environmental?.data;
  if (!environmental || environmental.wetlandAcreageBasis === 'source-feature-total'
      || (environmental.wetlandMappingCoverage && environmental.wetlandMappingCoverage.status !== 'complete')) return { acres: null, complete: false };
  if (environmental.hasWetlands === false) return { acres: 0, complete: true };
  const acres = finiteNonNegative(environmental.wetlandAcres);
  return acres == null ? { acres: null, complete: false } : { acres, complete: true };
}

function isSpecialFloodHazardZone(zone: string): boolean {
  const normalized = zone.trim().toUpperCase();
  return /^(?:A|AE|AH|AO|AR|A99|V|VE)(?:\b|[0-9-])/.test(normalized);
}

/**
 * Exact geometric FEMA flood overlap from the frozen SED-authority layout.
 * Available when the promoted scenario carried an APPLIED FEMA exclusion
 * snapshot (complete) or a PARTIAL one (the clip is still the retained
 * hazard geometry, but completeness is not claimed); preferred over the SED
 * zone-fraction heuristic because the zones were clipped to real parcel
 * geometry at retrieval time. Returns null when unavailable so the heuristic
 * remains the fallback. A partial set never discards the clip: the caller
 * keeps the larger of the clip and the heuristic with complete=false.
 */
function resolveExactFemaExclusionAcres(
  authority: GenerationImpactSedAuthoritySnapshot | null | undefined,
): { acres: number; complete: boolean } | null {
  const exclusions = authority?.scenarioSnapshot.input.layout?.exclusions;
  if (!authority || !exclusions || (exclusions.femaStatus !== 'applied' && exclusions.femaStatus !== 'partial')) return null;
  const complete = exclusions.femaStatus === 'applied';
  const femaZones = exclusions.zones.filter(zone => zone.source === 'fema-nfhl');
  // A partial set that retained no FEMA zone is not evidence of zero overlap:
  // the value stays unknown (null) so the heuristic, including its own null,
  // decides. Only an applied set may assert zero.
  if (femaZones.length === 0) return complete ? { acres: 0, complete } : null;
  try {
    const closeRing = (ring: ReadonlyArray<{ lat: number; lng: number }>): polygonClipping.Ring => {
      const positions = ring.map(point => [point.lng, point.lat] as [number, number]);
      const first = positions[0];
      const last = positions.at(-1);
      if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
        positions.push([...first]);
      }
      return positions;
    };
    const parcelGeometry: polygonClipping.MultiPolygon = authority.parcelBoundary.polygons
      .map(polygon => polygon.map(closeRing));
    let zoneGeometry: polygonClipping.MultiPolygon = [];
    for (const zone of femaZones) {
      for (const ring of zone.polygons) {
        if (ring.length < 3) continue;
        const geometry = [[closeRing(ring)]] as polygonClipping.MultiPolygon;
        zoneGeometry = zoneGeometry.length === 0
          ? geometry
          : polygonClipping.union(zoneGeometry, geometry);
      }
    }
    if (zoneGeometry.length === 0) return complete ? { acres: 0, complete } : null;
    const clipped = polygonClipping.intersection(zoneGeometry, parcelGeometry);
    const acres = clipped.reduce((total, polygon) => {
      const outer = polygon[0];
      if (!outer || outer.length < 4) return total;
      let area = polygonArea(outer.map(([lng, lat]) => ({ lat, lng })));
      for (let index = 1; index < polygon.length; index += 1) {
        const hole = polygon[index];
        if (hole && hole.length >= 4) {
          area -= polygonArea(hole.map(([lng, lat]) => ({ lat, lng })));
        }
      }
      return total + Math.max(0, area);
    }, 0);
    return { acres, complete };
  } catch {
    // Malformed frozen geometry: fall back to the SED heuristic rather than
    // reporting an exact zero.
    return null;
  }
}

function resolveFloodOverlapAcres(
  sed: SiteEvidenceDocument | null | undefined,
  parcelAreaAcres: number | null,
): { acres: number | null; complete: boolean } {
  const flood = sed?.flood?.data;
  if (!flood || parcelAreaAcres == null) return { acres: null, complete: false };
  // A flood record computed on a disclosed geometry fallback never supplies a
  // complete deduction basis: the deduction is carried but the fit stays
  // indeterminate, mirroring the wetland coverage gate.
  const fallbackGeometry = !!flood.areaAnalysis?.geometryTier;
  if (flood.sfha === false) return { acres: 0, complete: !fallbackGeometry };
  const overlap = flood.zoneOverlap;
  if (!Array.isArray(overlap) || overlap.length === 0) return { acres: null, complete: false };
  const fraction = overlap.reduce((sum, row) => (
    isSpecialFloodHazardZone(row.zone) && Number.isFinite(row.fraction)
      ? sum + Math.max(0, Math.min(1, row.fraction))
      : sum
  ), 0);
  return { acres: Math.min(1, fraction) * parcelAreaAcres, complete: !fallbackGeometry };
}

function resolveAcreageBasis(
  site: TestFitSite | null | undefined,
  sed: SiteEvidenceDocument | null | undefined,
  authority: GenerationImpactSedAuthoritySnapshot | null | undefined,
): AcreageBasis {
  const parcel = authority
    ? { acres: finitePositive(authority.parcelBoundary.areaAcres), source: 'sed-authority-boundary' as const }
    : resolveParcelArea(site, sed);
  if (parcel.acres == null) {
    return {
      parcelAreaAcres: null,
      parcelAreaSource: parcel.source,
      wetlandAcres: null,
      floodOverlapAcres: null,
      setbackAllowanceAcres: null,
      netBuildableAcres: null,
      netBuildableBasis: 'unavailable',
      constraintDataComplete: false,
    };
  }
  if (site?.studyArea || sed?.studyAreaScope) {
    return {
      parcelAreaAcres: parcel.acres,
      parcelAreaSource: parcel.source,
      wetlandAcres: null,
      floodOverlapAcres: resolveExactFemaExclusionAcres(authority)?.acres ?? null,
      setbackAllowanceAcres: null,
      netBuildableAcres: null,
      netBuildableBasis: 'unavailable',
      constraintDataComplete: false,
    };
  }
  const wetland = resolveWetlandAcres(sed);
  // An applied exclusion clip is authoritative. A partial clip is never
  // discarded for the smaller heuristic: the deduction is the larger of the
  // two and the basis stays incomplete.
  const exactFlood = resolveExactFemaExclusionAcres(authority);
  const heuristicFlood = resolveFloodOverlapAcres(sed, parcel.acres);
  // A flood record computed on a disclosed geometry fallback never supplies a
  // complete basis, whichever branch supplies the acreage.
  const fallbackFloodGeometry = !!sed?.flood?.data?.areaAnalysis?.geometryTier;
  const flood = exactFlood?.complete && !fallbackFloodGeometry
    ? exactFlood
    : exactFlood
      ? { acres: Math.max(exactFlood.acres, heuristicFlood.acres ?? 0), complete: false }
      : heuristicFlood;
  const setbackAllowanceAcres = parcel.acres * DEFAULT_SETBACK_ALLOWANCE_FRACTION;
  const netBuildableAcres = Math.max(
    0,
    parcel.acres - (wetland.acres ?? 0) - (flood.acres ?? 0) - setbackAllowanceAcres,
  );
  const constraintDataComplete = wetland.complete && flood.complete;
  return {
    parcelAreaAcres: parcel.acres,
    parcelAreaSource: parcel.source,
    wetlandAcres: wetland.acres,
    floodOverlapAcres: flood.acres,
    setbackAllowanceAcres,
    netBuildableAcres,
    netBuildableBasis: constraintDataComplete
      ? 'conservative-planning-screen'
      : 'partial-planning-screen',
    constraintDataComplete,
  };
}

function closeRing(points: readonly { lat: number; lng: number }[]): [number, number][] | null {
  const ring: [number, number][] = points
    .filter((point) => Number.isFinite(point?.lat) && Number.isFinite(point?.lng))
    .map((point) => [point.lng, point.lat]);
  if (ring.length < 3) return null;
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);
  if (new Set(ring.slice(0, -1).map(([x, y]) => `${x},${y}`)).size < 3) return null;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const start = ring[index]!;
    const end = ring[index + 1]!;
    if (start[0] === end[0] && start[1] === end[1]) return null;
  }
  const cross = (a: [number, number], b: [number, number], c: [number, number]): number =>
    (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const intersects = (
    a: [number, number],
    b: [number, number],
    c: [number, number],
    d: [number, number],
  ): boolean => {
    const abC = cross(a, b, c);
    const abD = cross(a, b, d);
    const cdA = cross(c, d, a);
    const cdB = cross(c, d, b);
    return ((abC > 0 && abD < 0) || (abC < 0 && abD > 0))
      && ((cdA > 0 && cdB < 0) || (cdA < 0 && cdB > 0));
  };
  const segmentCount = ring.length - 1;
  for (let left = 0; left < segmentCount; left += 1) {
    for (let right = left + 1; right < segmentCount; right += 1) {
      const adjacent = right === left + 1 || (left === 0 && right === segmentCount - 1);
      if (adjacent) continue;
      if (intersects(ring[left]!, ring[left + 1]!, ring[right]!, ring[right + 1]!)) return null;
    }
  }
  return ring;
}

function unionBoundary(
  site: TestFitSite | null | undefined,
  authority: GenerationImpactSedAuthoritySnapshot | null | undefined,
): polygonClipping.MultiPolygon | null {
  if (authority) {
    const frozenPolygons: polygonClipping.MultiPolygon = [];
    for (const polygon of authority.parcelBoundary.polygons) {
      const frozenRings: polygonClipping.Polygon = [];
      for (const points of polygon) {
        const ring = closeRing(points);
        if (!ring) return null;
        frozenRings.push(ring);
      }
      if (frozenRings.length === 0) return null;
      frozenPolygons.push(frozenRings);
    }
    return frozenPolygons.length > 0 ? frozenPolygons : null;
  }
  const rings = site?.siteBoundaries?.map((parcel) => parcel.polygon)
    ?? (site?.siteBoundary ? [site.siteBoundary] : []);
  const polygons = rings
    .map(closeRing)
    .filter((ring): ring is [number, number][] => ring != null)
    .map((ring) => [ring] as polygonClipping.Polygon);
  if (polygons.length === 0) return null;
  return polygonClipping.union(polygons[0]!, ...polygons.slice(1)) as polygonClipping.MultiPolygon;
}

function multiPolygonAreaAcres(multi: polygonClipping.MultiPolygon | null | undefined): number {
  if (!multi) return 0;
  let acres = 0;
  for (const polygon of multi) {
    const outer = polygon[0];
    if (!outer || outer.length < 3) continue;
    acres += polygonArea(outer.map(([lng, lat]) => ({ lat, lng })));
    for (let holeIndex = 1; holeIndex < polygon.length; holeIndex += 1) {
      const hole = polygon[holeIndex];
      if (hole && hole.length >= 3) {
        acres -= polygonArea(hole.map(([lng, lat]) => ({ lat, lng })));
      }
    }
  }
  return Math.max(0, acres);
}

function layoutRole(layer: SiteLayer): string | null {
  const role = layer.attributes?.layoutRole ?? layer.attributes?.generationImpactLogicalKey;
  return typeof role === 'string' ? role : null;
}

function requiredPhysicalRoles(scenario: GenerationImpactScenarioSnapshot): string[] {
  const roles = ['plant', 'switchyard'];
  if (scenario.result.footprint.ccsAcres + scenario.result.footprint.compressionAcres > AREA_TOLERANCE_ACRES) {
    roles.push('ccs');
  }
  return roles;
}

function evaluatePublishedGeometryUnsafe(
  scenario: GenerationImpactScenarioSnapshot,
  site: TestFitSite | null | undefined,
  authority: GenerationImpactSedAuthoritySnapshot | null | undefined,
): GeometryFit {
  const requiredRoles = requiredPhysicalRoles(scenario);
  if (!authority) {
    return {
      verdict: 'indeterminate',
      basis: 'unavailable',
      outsideBoundaryAcres: null,
      representedFootprintAcres: null,
      overlappingFootprintAcres: null,
      roles: [],
      reason: 'No validated published-layout authority was frozen into the SED.',
    };
  }
  const boundary = unionBoundary(site, authority);
  if (!boundary) {
    return {
      verdict: 'indeterminate',
      basis: 'unavailable',
      outsideBoundaryAcres: null,
      representedFootprintAcres: null,
      overlappingFootprintAcres: null,
      roles: [],
      reason: 'No parcel polygon was available for geometric containment testing.',
    };
  }

  const byRole = new Map<string, SiteLayer[]>();
  for (const layer of authority.layoutPublication.layers) {
    const role = layoutRole(layer);
    if (!role || layer.geometry.type !== 'polygon') continue;
    // Parcel-backed publications use stable granular keys such as
    // plant:generation-block-2 and ccs:compression. Test Fit assesses the
    // union for each frozen physical role, while legacy/fallback publications
    // continue to use the aggregate plant/switchyard/ccs keys directly.
    const physicalRole = role.split(':', 1)[0]!;
    if (!requiredRoles.includes(physicalRole)) continue;
    const grouped = byRole.get(physicalRole) ?? [];
    grouped.push(layer);
    byRole.set(physicalRole, grouped);
  }
  const missing = requiredRoles.filter((role) => !byRole.has(role));
  if (missing.length > 0) {
    return {
      verdict: 'indeterminate',
      basis: 'unavailable',
      outsideBoundaryAcres: null,
      representedFootprintAcres: null,
      overlappingFootprintAcres: null,
      roles: requiredRoles.filter((role) => byRole.has(role)),
      reason: `Published layout is missing required physical role${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}.`,
    };
  }

  let outsideBoundaryAcres = 0;
  let physicalUnion: polygonClipping.MultiPolygon | null = null;
  const expectedRoleArea = new Map<string, number>([
    ['plant', scenario.result.footprint.plantAcres + scenario.result.footprint.coolingAcres],
    ['switchyard', scenario.result.footprint.switchyardAcres],
    ['ccs', scenario.result.footprint.ccsAcres + scenario.result.footprint.compressionAcres],
  ]);
  for (const role of requiredRoles) {
    const layers = byRole.get(role)!;
    let roleUnion: polygonClipping.MultiPolygon | null = null;
    for (const layer of layers) {
      const ring = closeRing(layer.geometry.coordinates);
      if (!ring) {
        return {
          verdict: 'indeterminate',
          basis: 'unavailable',
          outsideBoundaryAcres: null,
          representedFootprintAcres: null,
          overlappingFootprintAcres: null,
          roles: requiredRoles,
          reason: `Published ${role} geometry is degenerate and cannot be containment-tested.`,
        };
      }
      const polygon = [ring] as polygonClipping.Polygon;
      roleUnion = roleUnion == null
        ? [polygon] as polygonClipping.MultiPolygon
        : polygonClipping.union(roleUnion, polygon) as polygonClipping.MultiPolygon;
    }
    const actualRoleArea = multiPolygonAreaAcres(roleUnion);
    const expected = expectedRoleArea.get(role) ?? 0;
    const roleTolerance = Math.max(AREA_TOLERANCE_ACRES, expected * 0.005);
    if (Math.abs(actualRoleArea - expected) > roleTolerance) {
      return {
        verdict: 'fail',
        basis: 'published-layout-within-site-boundary',
        outsideBoundaryAcres: null,
        representedFootprintAcres: null,
        overlappingFootprintAcres: null,
        roles: requiredRoles,
        reason: `Published ${role} polygon represents ${actualRoleArea.toFixed(2)} acres but the frozen scenario requires ${expected.toFixed(2)} acres.`,
      };
    }
    physicalUnion = physicalUnion == null
      ? roleUnion
      : polygonClipping.union(physicalUnion, roleUnion!) as polygonClipping.MultiPolygon;
    const outside = polygonClipping.difference(roleUnion!, boundary) as polygonClipping.MultiPolygon;
    outsideBoundaryAcres += multiPolygonAreaAcres(outside);
  }

  const measuredRepresentedFootprintAcres = multiPolygonAreaAcres(physicalUnion);
  const unionDelta = Math.abs(measuredRepresentedFootprintAcres - scenario.result.footprint.totalAcres);
  const unionTolerance = Math.max(AREA_TOLERANCE_ACRES, scenario.result.footprint.totalAcres * 0.005);
  // The layout publisher sizes degree-space polygons from metre-space acres;
  // normalize sub-tolerance geodesic projection drift to the frozen solver
  // total so a PASS never simultaneously reports a phantom overlap.
  const representedFootprintAcres = unionDelta <= unionTolerance
    ? scenario.result.footprint.totalAcres
    : measuredRepresentedFootprintAcres;
  const overlappingFootprintAcres = unionDelta <= unionTolerance
    ? 0
    : Math.max(0, scenario.result.footprint.totalAcres - measuredRepresentedFootprintAcres);
  const hasOutside = outsideBoundaryAcres > AREA_TOLERANCE_ACRES;
  const hasOverlapOrCoverageGap = unionDelta > unionTolerance;
  const verdict: EngineeringVerdict = hasOutside || hasOverlapOrCoverageGap ? 'fail' : 'pass';
  const reason = hasOutside
    ? `${outsideBoundaryAcres.toFixed(2)} acres of published physical generation geometry fall outside the frozen parcel boundary.`
    : hasOverlapOrCoverageGap
      ? `Published physical polygons cover ${representedFootprintAcres.toFixed(2)} unique acres versus the ${scenario.result.footprint.totalAcres.toFixed(2)}-acre frozen footprint; ${overlappingFootprintAcres.toFixed(2)} acres are spatially overlapped or otherwise not represented one-for-one.`
      : `All published physical generation polygons are contained within the frozen parcel boundary and represent the full ${representedFootprintAcres.toFixed(2)}-acre footprint without material overlap.`;
  return {
    verdict,
    basis: 'published-layout-within-site-boundary',
    outsideBoundaryAcres,
    representedFootprintAcres,
    overlappingFootprintAcres,
    roles: requiredRoles,
    reason,
  };
}

function evaluatePublishedGeometry(
  scenario: GenerationImpactScenarioSnapshot,
  site: TestFitSite | null | undefined,
  authority: GenerationImpactSedAuthoritySnapshot | null | undefined,
): GeometryFit {
  try {
    return evaluatePublishedGeometryUnsafe(scenario, site, authority);
  } catch (error) {
    const detail = error instanceof Error && error.message.trim()
      ? ` ${error.message.trim()}`
      : '';
    return {
      verdict: 'indeterminate',
      basis: 'unavailable',
      outsideBoundaryAcres: null,
      representedFootprintAcres: null,
      overlappingFootprintAcres: null,
      roles: [],
      reason: `Published-layout geometry could not be evaluated safely.${detail}`,
    };
  }
}

function acreageVerdict(
  totalAcres: number,
  basis: AcreageBasis,
): { verdict: EngineeringVerdict; margin: number | null } {
  if (basis.netBuildableAcres == null) return { verdict: 'indeterminate', margin: null };
  const margin = basis.netBuildableAcres - totalAcres;
  if (margin < -AREA_TOLERANCE_ACRES) return { verdict: 'fail', margin };
  return {
    verdict: basis.constraintDataComplete ? 'pass' : 'indeterminate',
    margin,
  };
}

/**
 * Project the exact frozen generation footprint onto Test Fit, then assess two
 * deliberately separate questions:
 *   1. acreage adequacy against the frozen net-buildable planning screen;
 *   2. geometric containment of every published physical layout polygon.
 * Neither verdict includes data halls or constitutes an overall campus fit.
 */
export function buildGenerationImpactTestFitProjection(
  input: GenerationImpactTestFitProjectionInput,
): GenerationImpactFootprintProjection {
  input = { ...input, site: input.site ? applySiteStudyArea(input.site) : input.site };
  const scenario = input.authority?.scenarioSnapshot ?? input.scenario;
  const footprint = input.authority?.footprint ?? input.scenario.result.footprint;
  const acreage = resolveAcreageBasis(input.site, input.sed, input.authority);
  const fit = acreageVerdict(footprint.totalAcres, acreage);
  const geometry = evaluatePublishedGeometry(scenario, input.site, input.authority);
  return {
    plantAcres: footprint.plantAcres,
    coolingAcres: footprint.coolingAcres,
    switchyardAcres: footprint.switchyardAcres,
    ccsAcres: footprint.ccsAcres,
    compressionAcres: footprint.compressionAcres,
    totalAcres: footprint.totalAcres,
    parcelAreaAcres: acreage.parcelAreaAcres,
    parcelAreaSource: acreage.parcelAreaSource,
    wetlandAcres: acreage.wetlandAcres,
    floodOverlapAcres: acreage.floodOverlapAcres,
    setbackAllowanceAcres: acreage.setbackAllowanceAcres,
    netBuildableAcres: acreage.netBuildableAcres,
    netBuildableBasis: acreage.netBuildableBasis,
    constraintDataComplete: acreage.constraintDataComplete,
    footprintMarginAcres: fit.margin,
    acreageFitVerdict: fit.verdict,
    spatialFitVerdict: geometry.verdict,
    spatialFitBasis: geometry.basis,
    outsideBoundaryAcres: geometry.outsideBoundaryAcres,
    representedFootprintAcres: geometry.representedFootprintAcres,
    overlappingFootprintAcres: geometry.overlappingFootprintAcres,
    assessedLayoutRoles: geometry.roles,
    spatialFitReason: `${geometry.reason} This is a generation-layout-only screen and does not determine whether the data halls or overall campus fit.`,
  };
}

function acres(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? 'Indeterminate' : `${value.toFixed(2)} acres`;
}

function technologyLabel(scenario: GenerationImpactScenarioSnapshot): string {
  return [...new Set(scenario.input.units.map((unit) => unit.technology))].join(' + ') || 'unavailable';
}

function totalNameplateMW(scenario: GenerationImpactScenarioSnapshot): number {
  return scenario.input.units.reduce(
    (total, unit) => total + Math.max(0, unit.count) * Math.max(0, unit.isoGrossMWPerUnit),
    0,
  );
}

function verdictLabel(verdict: EngineeringVerdict | undefined): string {
  return (verdict ?? 'indeterminate').toUpperCase();
}

function authoritativeSummary(projection: GenerationImpactFootprintProjection): string {
  return [
    `Generation-layout screen only: the frozen Generation & Impact footprint is ${acres(projection.totalAcres)}`,
    `(plant ${acres(projection.plantAcres)}, cooling ${acres(projection.coolingAcres)}, switchyard ${acres(projection.switchyardAcres)}, CCS ${acres(projection.ccsAcres)}, compression ${acres(projection.compressionAcres)}).`,
    `Generation-footprint acreage adequacy is ${verdictLabel(projection.acreageFitVerdict)} with ${acres(projection.footprintMarginAcres)} net-buildable margin.`,
    `Published-layout parcel containment is ${verdictLabel(projection.spatialFitVerdict)}.`,
    'These findings do not determine whether data halls, roads, stormwater facilities, or the overall campus fit.',
  ].join(' ');
}

export function stripModelGenerationFitClaims(value: unknown): string {
  const text = typeof value === 'string' ? value : '';
  return text
    .split(/(?<=[.!?])\s+/)
    .flatMap((sentence) => {
      const clauses = sentence.split(/;\s*|(?<=[,])\s+(?=(?:and|but|while|whereas)\b)/i);
      return clauses.filter((clause) => {
        const generationSubject = /\b(?:generation(?:\s+(?:plant|island|layout|footprint|blocks?))?|power\s+plant|plant(?:\s+(?:island|block|area|layout|footprint))?|btm(?:\s+(?:plant|island|layout|footprint))?|on[- ]site\s+(?:generation|power|plant)|switchyard|substation|electrical\s+yard|ccs|carbon[- ]capture|compression(?:\s+(?:block|area|footprint))?|turbine(?:\s+(?:island|block|area|footprint))?|generator(?:\s+(?:yard|block|area|footprint))?|fuel\s+storage|gas\s+yard|battery\s+yard)\b/i.test(clause);
        const fitOrAcreageClaim = /\b(?:\d+(?:\.\d+)?(?:\s*|-)(?:acres?|ac\b)|fits?\b|does\s+not\s+fit|can(?:not|'t)?\s+be\s+accommodated|(?:adequate|insufficient)\s+(?:room|space|land)|outside\s+(?:the\s+)?(?:parcel\s+)?boundary|within\s+(?:the\s+)?(?:parcel\s+)?boundary|inside\s+(?:the\s+)?(?:parcel|boundary)|contained(?:\s+(?:within|inside))?|footprint|spatial(?:ly)?|overlap(?:s|ped|ping)?|net[- ]buildable|acreage|land\s+(?:area|margin|requirement)|requires?\s+(?:additional\s+)?land|reserves?\s+(?:additional\s+)?land)\b/i.test(clause);
        return !(generationSubject && fitOrAcreageClaim);
      });
    })
    .join(' ')
    .replace(/^\s*(?:and|but|while|whereas)\s+/i, '')
    .replace(/\s+,/g, ',')
    .trim();
}

const TEST_FIT_TOP_LEVEL_PROSE_FIELDS = [
  'summary',
  'executiveSummary',
  'projectIntentOverview',
  'sitingNotes',
  'constructabilityAssessment',
  'costRangeEstimate',
  'bopEconomics',
  'maxCampusCapacity',
] as const;

const TEST_FIT_ANALYSIS_REQUIRED_FALLBACKS: Record<string, string> = {
  buildingPlacement: 'No non-generation building-placement conclusion survived reconciliation; validate data-hall placement against the frozen parcel constraints.',
  setbackCompliance: 'Setback compliance remains to be validated independently of the frozen generation-layout screen.',
  parkingAccess: 'Parking and emergency-access fit remain to be validated independently of the frozen generation-layout screen.',
  stormwaterDetention: 'Stormwater detention fit remains to be validated independently of the frozen generation-layout screen.',
  utilityRouting: 'Utility routing remains to be validated against the published generation layout and detailed campus design.',
  dataHallOrientation: 'Data-hall orientation remains to be validated independently of the generation-layout-only verdict.',
  phaseLayout: 'Campus phasing remains to be validated independently of the generation-layout-only verdict.',
};

function sanitizedOr(value: unknown, fallback: string): string {
  return stripModelGenerationFitClaims(value) || fallback;
}

function isEmptyNarrativeLeaf(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/**
 * Withheld narrative × Test Fit reconciliation (2026-09-18).
 *
 * Both Test Fit reconcilers run at the persistence boundary, AFTER the
 * reviewer-voice gate has withheld (null; '' for `summary`) a leaf and
 * recorded its exact path on the `reviewer-voice-leak` quality warning. A
 * withheld leaf must stay exactly that: never back-filled with a fallback
 * sentence ("Validate the frozen generation layout", "Campus fit follow-up",
 * a deterministic summary in `siteConstraints`) and never dropped from its
 * array, otherwise the "not assessed" marker disappears from the reader, the
 * frozen document and the frozen decision analysis while the hold record
 * still names the leaf (and a null `siteConstraints` crashed the job).
 *
 * Entries that do NOT hold a withheld leaf keep their exact pre-2026-09-18
 * handling, including removal when stripping leaves them empty. When such a
 * removal moves a later entry that DOES hold a withheld leaf, the recorded
 * path is rewritten to the entry's new index (label re-derived), so the
 * record keeps naming the leaf it withheld in the persisted body. With no
 * withheld record on the report this guard is the identity.
 */
interface WithheldLeafGuard {
  /** True when the gate withheld the leaf at `path` and it is still empty. */
  held(path: string, value: unknown): boolean;
  /** Strip generation fit claims unless the leaf is withheld (then: untouched). */
  strip<T>(path: string, value: T): T | string;
  /** `sanitizedOr` unless the leaf is withheld (then: untouched, no fallback). */
  sanitizedOr<T>(path: string, value: T, fallback: string): T | string;
  /**
   * Keep `keep(entry)` entries of the array at `arrayPath`, plus every entry
   * holding a withheld leaf. Returns kept entries paired with their ORIGINAL
   * index, and records the index shift for {@link commit}.
   */
  compact<T>(arrayPath: string, entries: readonly T[], keep: (entry: T) => boolean): Array<[T, number]>;
  /** Strip a string list, keeping withheld entries in place. */
  stripList(arrayPath: string, values: readonly unknown[]): string[];
  /** Rewrite recorded paths moved by {@link compact}. */
  commit(report: SiteRagReport): void;
}

function createWithheldLeafGuard(report: SiteRagReport): WithheldLeafGuard {
  const paths = new Set(withheldNarrativeLeaves(report).map((leaf) => leaf.path));
  const remaps: Array<{ arrayPath: string; indexMap: Map<number, number> }> = [];
  const isAtOrUnder = (path: string, prefix: string): boolean =>
    path === prefix || path.startsWith(`${prefix}.`) || path.startsWith(`${prefix}[`);
  const holdsUnder = (prefix: string): boolean => {
    for (const path of paths) if (isAtOrUnder(path, prefix)) return true;
    return false;
  };
  const guard: WithheldLeafGuard = {
    held: (path, value) => paths.has(path) && isEmptyNarrativeLeaf(value),
    strip: (path, value) => (guard.held(path, value) ? value : stripModelGenerationFitClaims(value)),
    sanitizedOr: (path, value, fallback) => (guard.held(path, value) ? value : sanitizedOr(value, fallback)),
    compact: (arrayPath, entries, keep) => {
      const kept: Array<[(typeof entries)[number], number]> = [];
      const indexMap = new Map<number, number>();
      entries.forEach((entry, index) => {
        if (!holdsUnder(`${arrayPath}[${index}]`) && !keep(entry)) return;
        indexMap.set(index, kept.length);
        kept.push([entry, index]);
      });
      if (paths.size > 0 && [...indexMap].some(([from, to]) => from !== to)) {
        remaps.push({ arrayPath, indexMap });
      }
      return kept;
    },
    stripList: (arrayPath, values) => guard
      .compact(
        arrayPath,
        values.map((value, index) => guard.strip(`${arrayPath}[${index}]`, value)),
        Boolean,
      )
      .map(([value]) => value as string),
    commit: (target) => {
      if (remaps.length === 0 || !Array.isArray(target.qualityWarnings)) return;
      const remapPath = (path: string): string => {
        let next = path;
        for (const { arrayPath, indexMap } of remaps) {
          for (const [from, to] of indexMap) {
            const prefix = `${arrayPath}[${from}]`;
            if (from !== to && isAtOrUnder(next, prefix)) {
              next = `${arrayPath}[${to}]${next.slice(prefix.length)}`;
              break;
            }
          }
        }
        return next;
      };
      target.qualityWarnings = target.qualityWarnings.map((warning) => {
        if (warning?.type !== 'reviewer-voice-leak' || !Array.isArray(warning.withheld)) return warning;
        let changed = false;
        const withheld = warning.withheld.map((leaf) => {
          const path = remapPath(leaf.path);
          if (path === leaf.path) return leaf;
          changed = true;
          const segments = parseReportLeafPath(path);
          return { ...leaf, path, label: segments ? reportLeafLabel(segments) : leaf.label };
        });
        return changed ? { ...warning, withheld } : warning;
      });
    },
  };
  return guard;
}

/** Remove model-authored generation fit/acreage claims from every Test Fit prose carrier. */
function sanitizeTestFitGenerationClaims(report: SiteRagReport, withheld: WithheldLeafGuard): void {
  const reportRecord = report as unknown as Record<string, unknown>;
  for (const field of TEST_FIT_TOP_LEVEL_PROSE_FIELDS) {
    if (typeof reportRecord[field] === 'string') {
      reportRecord[field] = stripModelGenerationFitClaims(reportRecord[field]);
    }
  }

  (report.recommendations ?? []).forEach((recommendation, index) => {
    const at = (field: string) => `recommendations[${index}].${field}`;
    recommendation.title = withheld.sanitizedOr(
      at('title'),
      recommendation.title,
      'Validate the frozen generation layout',
    );
    recommendation.description = withheld.sanitizedOr(
      at('description'),
      recommendation.description,
      'Use the frozen Generation & Impact projection for generation acreage and spatial-fit decisions.',
    );
    recommendation.rationale = withheld.sanitizedOr(
      at('rationale'),
      recommendation.rationale,
      'Model-authored generation fit claims were superseded by the deterministic report projection.',
    );
    if (recommendation.architecture) {
      recommendation.architecture = sanitizedOr(recommendation.architecture, 'Frozen Generation & Impact scenario');
    }
  });
  for (const risk of report.risks ?? []) {
    risk.label = sanitizedOr(risk.label, 'Generation layout validation');
    risk.impact = sanitizedOr(
      risk.impact,
      'Generation acreage and spatial fit are governed by the frozen Generation & Impact projection.',
    );
    risk.mitigation = sanitizedOr(
      risk.mitigation,
      'Validate detailed campus design against the frozen generation-layout basis.',
    );
    if (risk.triggeringValue) {
      risk.triggeringValue = sanitizedOr(risk.triggeringValue, 'Frozen Generation & Impact projection');
    }
  }
  for (const finding of report.structuredFindings ?? []) {
    finding.title = sanitizedOr(finding.title, 'Frozen generation-layout basis');
    finding.detail = sanitizedOr(
      finding.detail,
      'Generation acreage and spatial fit are reported in the deterministic Generation & Impact projection.',
    );
    finding.impact = sanitizedOr(
      finding.impact,
      'The generation result remains separate from the overall campus fit decision.',
    );
  }
  for (const finding of report.contextualKeyFindings ?? []) {
    finding.title = sanitizedOr(finding.title, 'Frozen generation-layout basis');
    finding.detail = sanitizedOr(finding.detail, 'Use the deterministic Generation & Impact projection.');
    finding.intentContext = sanitizedOr(finding.intentContext, 'Promoted Generation & Impact scenario.');
    finding.influences = sanitizedOr(finding.influences, 'Informs generation layout only, not overall campus fit.');
    finding.whyKey = sanitizedOr(finding.whyKey, 'The frozen projection prevents scenario and layout drift.');
    finding.validationWarning = sanitizedOr(finding.validationWarning, 'Validate detailed campus fit independently.');
  }
  for (const claim of report.claims ?? []) {
    claim.text = stripModelGenerationFitClaims(claim.text)
      || 'Generation acreage and spatial-fit claim superseded by the frozen Generation & Impact projection.';
  }
  if (report.conclusion) {
    report.conclusion.topStrengths = withheld.stripList('conclusion.topStrengths', report.conclusion.topStrengths);
    report.conclusion.topRisks = withheld.stripList('conclusion.topRisks', report.conclusion.topRisks);
    if (report.conclusion.topConstraints) {
      report.conclusion.topConstraints = withheld.stripList(
        'conclusion.topConstraints',
        report.conclusion.topConstraints,
      );
    }
    if (report.conclusion.keyOpportunities) {
      report.conclusion.keyOpportunities = withheld.stripList(
        'conclusion.keyOpportunities',
        report.conclusion.keyOpportunities,
      );
    }
    if (report.conclusion.environmentalVerdict) {
      report.conclusion.environmentalVerdict = stripModelGenerationFitClaims(report.conclusion.environmentalVerdict);
    }
  }
  if (report.editorialNarrative) {
    for (const layer of Object.values(report.editorialNarrative.sections)) {
      if (layer.editorialHeadline) layer.editorialHeadline = stripModelGenerationFitClaims(layer.editorialHeadline);
      if (layer.editorialThesis) layer.editorialThesis = stripModelGenerationFitClaims(layer.editorialThesis);
      if (layer.editorialNarrative) layer.editorialNarrative = stripModelGenerationFitClaims(layer.editorialNarrative);
      if (layer.pullQuote?.text) layer.pullQuote.text = stripModelGenerationFitClaims(layer.pullQuote.text);
      for (const claim of layer.editorialClaims ?? []) {
        claim.text = stripModelGenerationFitClaims(claim.text)
          || 'Generation acreage and spatial-fit claim superseded by the frozen Generation & Impact projection.';
      }
    }
  }
}

export type TestFitWithoutGenerationImpactReason = 'grid-only' | 'undecided';

const TEST_FIT_WITHOUT_GENERATION_IMPACT_STATEMENTS = {
  'grid-only': 'Generation & Impact is not applicable because Project Intent declares a grid-only power path. No on-site generation acreage or layout-fit verdict is asserted.',
  undecided: 'Generation & Impact was not applied because Project Intent had not resolved grid versus on-site power when this Test Fit was generated. No on-site generation acreage or layout-fit verdict is asserted.',
} as const satisfies Record<TestFitWithoutGenerationImpactReason, string>;

/** Non-string input (a withheld null, a malformed legacy body) yields ''. */
function stripExistingGenerationImpactApplicabilityStatement(value: unknown): string {
  if (typeof value !== 'string') return '';
  return Object.values(TEST_FIT_WITHOUT_GENERATION_IMPACT_STATEMENTS)
    .reduce((remaining, statement) => remaining.split(statement).join(' '), value)
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Fail-closed reconciliation for a Test Fit without Generation & Impact.
 *
 * A narrative model may still emit generic BTM acreage or plant-fit prose when
 * no Generation & Impact scenario applies. Remove those claims from every
 * prose carrier, retain unrelated campus-fit analysis, clear structured
 * generation carriers, and stamp an applicability-accurate statement.
 */
export function reconcileTestFitReportWithoutGenerationImpact(
  report: SiteRagReport,
  reason: TestFitWithoutGenerationImpactReason,
): SiteRagReport {
  const withheld = createWithheldLeafGuard(report);
  const reportRecord = report as unknown as Record<string, unknown>;
  for (const field of TEST_FIT_TOP_LEVEL_PROSE_FIELDS) {
    if (typeof reportRecord[field] === 'string') {
      reportRecord[field] = stripModelGenerationFitClaims(reportRecord[field]);
    }
  }

  report.recommendations = withheld
    .compact(
      'recommendations',
      (report.recommendations ?? []).map((recommendation, index) => {
        const at = (field: string) => `recommendations[${index}].${field}`;
        return {
          ...recommendation,
          title: withheld.strip(at('title'), recommendation.title),
          description: withheld.strip(at('description'), recommendation.description),
          rationale: withheld.strip(at('rationale'), recommendation.rationale),
          ...(recommendation.architecture
            ? { architecture: stripModelGenerationFitClaims(recommendation.architecture) }
            : {}),
        };
      }),
      recommendation => Boolean(
        recommendation.title
        || recommendation.description
        || recommendation.rationale
        || recommendation.architecture,
      ),
    )
    .map(([recommendation, index]) => ({
      ...recommendation,
      title: withheld.held(`recommendations[${index}].title`, recommendation.title)
        ? recommendation.title
        : recommendation.title || 'Campus fit follow-up',
    }));
  report.risks = (report.risks ?? [])
    .map(risk => ({
      ...risk,
      label: stripModelGenerationFitClaims(risk.label),
      impact: stripModelGenerationFitClaims(risk.impact),
      mitigation: stripModelGenerationFitClaims(risk.mitigation),
      ...(risk.triggeringValue
        ? { triggeringValue: stripModelGenerationFitClaims(risk.triggeringValue) }
        : {}),
    }))
    .filter(risk => Boolean(risk.label || risk.impact || risk.mitigation || risk.triggeringValue))
    .map(risk => ({
      ...risk,
      label: risk.label || 'Campus fit risk',
    }));
  report.structuredFindings = (report.structuredFindings ?? [])
    .map(finding => ({
      ...finding,
      title: stripModelGenerationFitClaims(finding.title),
      detail: stripModelGenerationFitClaims(finding.detail),
      impact: stripModelGenerationFitClaims(finding.impact),
    }))
    .filter(finding => Boolean(finding.title || finding.detail || finding.impact))
    .map(finding => ({
      ...finding,
      title: finding.title || 'Campus fit finding',
    }));
  report.contextualKeyFindings = (report.contextualKeyFindings ?? [])
    .map(finding => ({
      ...finding,
      title: stripModelGenerationFitClaims(finding.title),
      detail: stripModelGenerationFitClaims(finding.detail),
      intentContext: stripModelGenerationFitClaims(finding.intentContext),
      influences: stripModelGenerationFitClaims(finding.influences),
      whyKey: stripModelGenerationFitClaims(finding.whyKey),
      validationWarning: stripModelGenerationFitClaims(finding.validationWarning),
    }))
    .filter(finding => Boolean(
      finding.title
      || finding.detail
      || finding.intentContext
      || finding.influences
      || finding.whyKey
      || finding.validationWarning,
    ))
    .map(finding => ({
      ...finding,
      title: finding.title || 'Campus fit finding',
    }));
  report.claims = (report.claims ?? [])
    .map(claim => ({ ...claim, text: stripModelGenerationFitClaims(claim.text) }))
    .filter(claim => Boolean(claim.text));

  if (report.conclusion) {
    report.conclusion.topStrengths = withheld.stripList('conclusion.topStrengths', report.conclusion.topStrengths);
    report.conclusion.topRisks = withheld.stripList('conclusion.topRisks', report.conclusion.topRisks);
    if (report.conclusion.topConstraints) {
      report.conclusion.topConstraints = withheld.stripList(
        'conclusion.topConstraints',
        report.conclusion.topConstraints,
      );
    }
    if (report.conclusion.keyOpportunities) {
      report.conclusion.keyOpportunities = withheld.stripList(
        'conclusion.keyOpportunities',
        report.conclusion.keyOpportunities,
      );
    }
    if (report.conclusion.environmentalVerdict) {
      report.conclusion.environmentalVerdict = stripModelGenerationFitClaims(
        report.conclusion.environmentalVerdict,
      );
    }
  }
  if (report.editorialNarrative) {
    for (const layer of Object.values(report.editorialNarrative.sections)) {
      if (layer.editorialHeadline) {
        layer.editorialHeadline = stripModelGenerationFitClaims(layer.editorialHeadline);
      }
      if (layer.editorialThesis) {
        layer.editorialThesis = stripModelGenerationFitClaims(layer.editorialThesis);
      }
      if (layer.editorialNarrative) {
        layer.editorialNarrative = stripModelGenerationFitClaims(layer.editorialNarrative);
      }
      if (layer.pullQuote?.text) {
        layer.pullQuote.text = stripModelGenerationFitClaims(layer.pullQuote.text);
      }
      layer.editorialClaims = (layer.editorialClaims ?? [])
        .map(claim => ({ ...claim, text: stripModelGenerationFitClaims(claim.text) }))
        .filter(claim => Boolean(claim.text));
    }
  }

  const notApplicable = TEST_FIT_WITHOUT_GENERATION_IMPACT_STATEMENTS[reason];
  if (report.testFitAnalysis) {
    const analysis = report.testFitAnalysis as unknown as Record<string, unknown>;
    for (const [field, value] of Object.entries(analysis)) {
      if (field !== 'generationFootprint'
        && field !== 'siteConstraints'
        && typeof value === 'string') {
        analysis[field] = stripModelGenerationFitClaims(value);
      }
    }
    // A withheld Parcel Overview stays withheld (null): the applicability
    // statement is not a substitute for the parcel narrative, and a non-empty
    // leaf would hide the "not assessed" marker the hold record still names.
    if (!withheld.held('testFitAnalysis.siteConstraints', report.testFitAnalysis.siteConstraints)) {
      const existingConstraints = stripExistingGenerationImpactApplicabilityStatement(
        report.testFitAnalysis.siteConstraints,
      );
      report.testFitAnalysis.siteConstraints = [
        notApplicable,
        stripModelGenerationFitClaims(existingConstraints),
      ].filter(Boolean).join(' ');
    }
    report.testFitAnalysis.recommendations = withheld.stripList(
      'testFitAnalysis.recommendations',
      Array.isArray(report.testFitAnalysis.recommendations) ? report.testFitAnalysis.recommendations : [],
    );
    delete report.testFitAnalysis.generationFootprint;
  }

  delete report.generationImpactScenarioAtGen;
  delete report.generationImpactProjection;
  delete report.generationImpactAtGen;
  delete report.generationLayoutExhibitAtGen;
  delete report.generationLayoutExhibitPublicAtGen;
  delete report.testFitAuthorityRef;
  withheld.commit(report);
  return report;
}

/** Replace model-authored generation acreage/verdict strings at persistence. */
export function reconcileTestFitReportWithGenerationImpact(
  report: SiteRagReport,
  scenario: GenerationImpactScenarioSnapshot,
  projection: GenerationImpactFootprintProjection,
  authority?: GenerationImpactSedAuthoritySnapshot | null,
): SiteRagReport {
  const existing = report.testFitAnalysis;
  const summary = authoritativeSummary(projection);
  const frozenSource = report.generationImpactProjection?.source;
  const basis = authority
    ? `SED authority v${authority.contractVersion}; scenario ${scenario.id} (${scenario.resultHash}); published layout v${authority.provenance.layoutVersion} (${authority.provenance.layoutPublicationId}, ${authority.provenance.layoutHash}).`
    : frozenSource?.layoutPublicationId && frozenSource.layoutVersion != null
      ? `SED authority v${frozenSource.sedAuthorityContractVersion ?? 'unknown'}; scenario ${scenario.id} (${scenario.resultHash}); published layout v${frozenSource.layoutVersion} (${frozenSource.layoutPublicationId}${frozenSource.layoutHash ? `, ${frozenSource.layoutHash}` : ''}).`
    : `Promoted scenario ${scenario.id} (${scenario.resultHash}); published-layout SED authority unavailable.`;
  const normalized = existing ?? {
    siteConstraints: '',
    buildingPlacement: '',
    setbackCompliance: '',
    parkingAccess: '',
    stormwaterDetention: '',
    utilityRouting: '',
    dataHallOrientation: '',
    phaseLayout: '',
    recommendations: [],
  };
  const withheld = createWithheldLeafGuard(report);
  const normalizedRecord = normalized as unknown as Record<string, unknown>;
  for (const [field, value] of Object.entries(normalizedRecord)) {
    if (field !== 'generationFootprint' && typeof value === 'string') {
      normalizedRecord[field] = field in TEST_FIT_ANALYSIS_REQUIRED_FALLBACKS
        ? sanitizedOr(value, TEST_FIT_ANALYSIS_REQUIRED_FALLBACKS[field]!)
        : stripModelGenerationFitClaims(value);
    }
  }
  // A withheld Parcel Overview stays withheld (null). The deterministic
  // footprint still renders from `generationImpactProjection.testFitFootprint`
  // (GenerationFootprintBlock) and `generationFootprint` below; writing the
  // summary here would hide the "not assessed" marker for the parcel narrative.
  if (!withheld.held('testFitAnalysis.siteConstraints', normalized.siteConstraints)) {
    normalized.siteConstraints = [summary, normalized.siteConstraints]
      .filter(Boolean)
      .join(' ');
  }
  // Non-string entries were always dropped; a withheld (null) entry is kept.
  normalized.recommendations = withheld.stripList(
    'testFitAnalysis.recommendations',
    Array.isArray(normalized.recommendations) ? normalized.recommendations : [],
  );
  normalized.generationFootprint = {
    chosenTechnology: technologyLabel(scenario),
    generationNameplateMW: `${totalNameplateMW(scenario).toFixed(2)} MW`,
    generationPlantAcres: acres(projection.plantAcres),
    coolingFootprintAcres: acres(projection.coolingAcres),
    substationFootprintAcres: acres(projection.switchyardAcres),
    ccsFootprintAcres: acres(projection.ccsAcres),
    compressionFootprintAcres: acres(projection.compressionAcres),
    totalGenerationAcres: acres(projection.totalAcres),
    parcelAreaAcres: acres(projection.parcelAreaAcres),
    netBuildableAcres: acres(projection.netBuildableAcres),
    footprintMarginAcres: acres(projection.footprintMarginAcres),
    representedFootprintAcres: acres(projection.representedFootprintAcres),
    overlappingFootprintAcres: acres(projection.overlappingFootprintAcres),
    outsideBoundaryAcres: acres(projection.outsideBoundaryAcres),
    acreageFitVerdict: projection.acreageFitVerdict ?? 'indeterminate',
    spatialFitVerdict: projection.spatialFitVerdict ?? 'indeterminate',
    spatialFitScope: 'generation-layout-only',
    basis,
  };
  report.testFitAnalysis = normalized;
  sanitizeTestFitGenerationClaims(report, withheld);
  withheld.commit(report);
  return report;
}

/** Exact basis injected into the typed Test Fit prompt. */
export function buildGenerationImpactTestFitPromptBlock(
  scenario: GenerationImpactScenarioSnapshot,
  projection: GenerationImpactFootprintProjection,
  authority?: GenerationImpactSedAuthoritySnapshot | null,
  authorityRef?: TestFitAuthorityRef | null,
): string {
  const provenance = authority
    ? `SED authority v${authority.contractVersion}; published layout v${authority.provenance.layoutVersion} ${authority.provenance.layoutPublicationId}; layout hash ${authority.provenance.layoutHash}; frozen parcel boundary ${authority.provenance.parcelBoundaryHash} (${authority.provenance.parcelBoundaryAreaAcres.toFixed(2)} acres, ${authority.provenance.parcelBoundarySource}, schema v${authority.provenance.parcelBoundarySchemaVersion})`
    : 'promoted scenario only; published-layout authority unavailable';
  const compactAuthority = authorityRef
    ? ` Test Fit authority: SED ${authorityRef.sedId} v${authorityRef.sedVersion}; scenario updated ${authorityRef.scenarioUpdatedAt}; input ${authorityRef.inputHash}; result ${authorityRef.resultHash}; model ${authorityRef.modelVersion}; promoted ${authorityRef.promotedAt}; layout ${authorityRef.layoutPublicationId} v${authorityRef.layoutVersion} (${authorityRef.layoutHash}); boundary ${authorityRef.boundaryHash}.`
    : '';
  return `═══ FROZEN GENERATION & IMPACT TEST-FIT BASIS (AUTHORITATIVE — USE VERBATIM) ═══
Scenario: ${scenario.name} (${scenario.id}); input ${scenario.inputHash}; result ${scenario.resultHash}; ${provenance}.${compactAuthority}
Technology: ${technologyLabel(scenario)}; installed ISO nameplate: ${totalNameplateMW(scenario).toFixed(2)} MW.
Full generation footprint: plant ${projection.plantAcres.toFixed(2)} ac + cooling ${projection.coolingAcres.toFixed(2)} ac + switchyard ${projection.switchyardAcres.toFixed(2)} ac + CCS ${projection.ccsAcres.toFixed(2)} ac + compression ${projection.compressionAcres.toFixed(2)} ac = TOTAL ${projection.totalAcres.toFixed(2)} ac.
Parcel/boundary area: ${acres(projection.parcelAreaAcres)} (${projection.parcelAreaSource ?? 'unavailable'}). Net-buildable planning screen: ${acres(projection.netBuildableAcres)}; generation-footprint margin: ${acres(projection.footprintMarginAcres)}; acreage adequacy: ${verdictLabel(projection.acreageFitVerdict)}.
Published-layout geometric containment: ${verdictLabel(projection.spatialFitVerdict)}; unique physical footprint represented: ${acres(projection.representedFootprintAcres)}; overlapped or unrepresented: ${acres(projection.overlappingFootprintAcres)}; outside parcel boundary: ${acres(projection.outsideBoundaryAcres)}. ${projection.spatialFitReason ?? ''}
${projection.constraintDataComplete === false || projection.netBuildableBasis === 'partial-planning-screen' || projection.netBuildableBasis === 'unavailable'
    ? `NET-BUILDABLE BASIS: ${projection.netBuildableBasis === 'unavailable'
      ? 'not established for this scope — the deductions above are partial inputs, not a usable-land figure. Do not publish a net-buildable acreage or an acreage adequacy pass from them.'
      : 'PARTIAL planning screen — at least one constraint input is incomplete (for example FEMA flood geometry computed on a disclosed fallback after an exact-precision clipping failure, or a screening that is not complete). State that the net-buildable figure rests on an incomplete constraint basis; never present it as an exact or complete usable-land figure.'}\n`
    : ''}BINDING RULES:
- Populate testFitAnalysis.generationFootprint from these figures verbatim. Do not re-derive generation acreage from Project Intent, targetFootprintAcres, MW×acre heuristics, or technology-density tables.
- Keep acreage adequacy separate from geometric containment. Never convert an acreage-only pass into a spatial-fit pass.
- Both verdicts are GENERATION-LAYOUT-ONLY. They do not prove that data halls, roads, setbacks, stormwater, or the overall campus fit.`;
}
